import { uniqueId } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Col, Container, Form, ListGroup, Modal, ProgressBar, Row } from 'react-bootstrap';
import { CheckCircleFill, CheckLg, Circle, Pen, PlusCircle, Trash } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import useDebouncedEffect from '../../hooks/useDebouncedEffect';
import AlertModal from '../AlertModal';
import Loader from '../Loader';
import SlidingSideBar from '../SlidingSideBar/SlidingSideBar';
import EntityCRUD from '../common/EntityCRUD';
import { getRows } from './constants';

const COLUMN_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes / No' }
];

const ENTITY_MODELS = ['Contract', 'Contact', 'Inventory'];

const TYPE_EXTRAS = {
  number: { type: 'number' },
  // formType text: FormGenerator has no 'email'/'date'/'phone' field types — use TextInput + variant
  email: { type: 'email', formType: 'text', variant: 'email' },
  phone: { type: 'phone', formType: 'text' },
  date: { type: 'date', formType: 'text', variant: 'date' },
  boolean: { type: 'boolean', showAsBadge: true, showLabel: true }
};

const keyify = (value = '') =>
  value
    .toString()
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part, index) =>
      index === 0 ? part.toLowerCase() : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    )
    .join('');

const emptyColumn = () => ({ uuid: uniqueId(), label: '', key: '', type: 'text', required: false });

const toEditableColumns = (columns = []) =>
  columns.length
    ? columns.map(column => ({
        uuid: uniqueId(),
        label: column.label || '',
        key: column.key || '',
        persistedKey: Boolean(column.key),
        type: column.type || 'text',
        required: Boolean(column.required)
      }))
    : [emptyColumn()];

const columnToHeader = column => ({
  name: column.label,
  key: column.key,
  required: Boolean(column.required),
  label: column.label,
  ...(TYPE_EXTRAS[column.type] || { type: 'text' })
});

const validateColumns = columns => {
  if (!columns.length) return 'Add at least one column.';

  const keys = new Set();
  for (const column of columns) {
    const label = (column.label || '').trim();
    if (!label) return 'Every column needs a label.';

    const key = (column.key || keyify(label) || '').trim();
    if (!key) return `Could not create a field key from '${label}'. Use letters or numbers.`;
    if (keys.has(key)) return `Column label '${label}' resolves to a duplicate field. Use a different label.`;
    keys.add(key);
  }
  return null;
};

const CreateEntityModal = ({ show, onHide, onSave, saving }) => {
  const [name, setName] = useState('');

  useEffect(() => {
    if (show) setName('');
  }, [show]);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Entity name is required.');
      return;
    }
    onSave({ name: trimmed, columns: [] });
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton={!saving}>
        <Modal.Title>
          <h6 className="mb-0">Create Custom Entity</h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label className="midFont mb-1">Entity Name</Form.Label>
          <Form.Control
            size="sm"
            autoFocus
            value={name}
            disabled={saving}
            placeholder="e.g. Title Companies"
            onChange={e => setName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSave();
              }
            }}
          />
        </Form.Group>
        {saving && <ProgressBar className="mt-3" now={100} animated striped label="Creating entity..." />}
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" variant="secondary" disabled={saving} onClick={onHide}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" disabled={saving} onClick={handleSave}>
          Create
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const SchemaEditorSidebar = ({ show, definition, onHide, onSaved, saving, setSaving }) => {
  const [columns, setColumns] = useState(() => toEditableColumns(definition?.columns));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!show || !definition) return;
    setColumns(toEditableColumns(definition.columns));
    setDirty(false);
  }, [show, definition?._id, definition?.updatedAt]);

  if (!definition) return null;

  const updateColumn = (uuid, patch) => {
    setDirty(true);
    setColumns(prev =>
      prev.map(column => {
        if (column.uuid !== uuid) return column;
        if (column.persistedKey && patch.type !== undefined) return column;
        const next = { ...column, ...patch };
        if (patch.label !== undefined && !column.persistedKey) {
          next.key = keyify(patch.label);
        }
        return next;
      })
    );
  };

  const handleSave = async () => {
    const error = validateColumns(columns);
    if (error) {
      toast.error(error);
      return;
    }

    setSaving(true);
    const { response, error: apiError } = await makeApiRequests({
      endpoint: ENDPOINTS.ENTITY_DEFINITION_WITH_ID(definition._id),
      method: 'PUT',
      requestBody: {
        name: definition.name,
        columns: columns.map(column => ({
          label: column.label.trim(),
          key: (column.persistedKey ? column.key : keyify(column.label)).trim(),
          type: column.type || 'text',
          required: Boolean(column.required)
        }))
      }
    });
    setSaving(false);

    if (apiError) {
      toast.error(apiError);
      return;
    }

    toast.success('Columns saved.');
    setDirty(false);
    onSaved(response);
    onHide();
  };

  const hasExistingSchema = (definition.columns || []).length > 0;

  return (
    <SlidingSideBar
      visible={show}
      onClose={onHide}
      showCloseButton={!saving}
      title={hasExistingSchema ? `Edit Columns · ${definition.name}` : `Define Columns · ${definition.name}`}
      width={50}
    >
      {show && (
        <div className="p-3">
          <p className="text-muted small mb-3">
            Define the fields for this entity. Saved columns keep their type; new columns can be typed before the first
            save.
          </p>

          <div className="d-flex justify-content-end mb-3">
            <Button
              size="sm"
              variant="outline-success"
              className="mr-2"
              disabled={saving}
              onClick={() => {
                setDirty(true);
                setColumns(prev => [...prev, emptyColumn()]);
              }}
            >
              <PlusCircle className="mr-1" /> Add Column
            </Button>
            <Button size="sm" variant="primary" disabled={saving || !dirty} onClick={handleSave}>
              Save Columns
            </Button>
          </div>

          {columns.map((column, index) => (
            <Card key={column.uuid} className="mb-2">
              <Card.Body className="py-2 px-3">
                <Row className="align-items-end">
                  <Col xs={12} md={5} className="mb-2 mb-md-0">
                    <Form.Label className="small mb-1">Label</Form.Label>
                    <Form.Control
                      size="sm"
                      value={column.label}
                      disabled={saving}
                      placeholder={`Column ${index + 1}`}
                      onChange={e => updateColumn(column.uuid, { label: e.target.value })}
                    />
                  </Col>
                  <Col xs={8} md={4} className="mb-2 mb-md-0">
                    <Form.Label className="small mb-1">Type</Form.Label>
                    <Form.Control
                      as="select"
                      size="sm"
                      value={column.type}
                      disabled={saving || column.persistedKey}
                      title={column.persistedKey ? 'Type cannot be changed after save' : undefined}
                      onChange={e => updateColumn(column.uuid, { type: e.target.value })}
                    >
                      {COLUMN_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Form.Control>
                  </Col>
                  <Col xs={4} md={3} className="d-flex align-items-center justify-content-between">
                    <Form.Check
                      type="checkbox"
                      id={`required-${column.uuid}`}
                      label="Required"
                      checked={column.required}
                      disabled={saving}
                      onChange={e => updateColumn(column.uuid, { required: e.target.checked })}
                    />
                    <Button
                      size="sm"
                      variant="outline-danger"
                      className="px-1 py-0"
                      disabled={saving || columns.length === 1}
                      onClick={() => {
                        setDirty(true);
                        setColumns(prev => prev.filter(c => c.uuid !== column.uuid));
                      }}
                    >
                      <Trash size={12} />
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ))}

          {saving && <ProgressBar className="mt-2" now={100} animated striped label="Saving columns..." />}
        </div>
      )}
    </SlidingSideBar>
  );
};

const CustomEntities = () => {
  const [loading, setLoading] = useState(true);
  const [definitions, setDefinitions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [savingEntity, setSavingEntity] = useState(false);
  const [savingColumns, setSavingColumns] = useState(false);
  const [showSchemaSidebar, setShowSchemaSidebar] = useState(false);
  const [toBeDeleted, setToBeDeleted] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [selectedModels, setSelectedModels] = useState([]);
  const editingIdRef = useRef(null);

  const activeDefinition = useMemo(() => definitions.find(definition => definition._id === activeId) || null, [
    definitions,
    activeId
  ]);

  const fetchDefinitions = async ({ preferId } = {}) => {
    setLoading(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.ENTITY_DEFINITIONS_LIST,
      method: 'POST'
    });
    setLoading(false);

    if (error) {
      toast.error(error);
      return;
    }

    const next = response || [];
    setDefinitions(next);
    setActiveId((next.find(d => d._id === preferId) || next.find(d => d._id === activeId) || next[0])?._id || null);
  };

  useEffect(() => {
    fetchDefinitions();
  }, []);

  const createEntity = async form => {
    setSavingEntity(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.ENTITY_DEFINITIONS_BASE,
      method: 'POST',
      requestBody: form
    });
    setSavingEntity(false);

    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Entity created.');
    setShowCreateModal(false);
    await fetchDefinitions({ preferId: response?._id });
  };

  const onColumnsSaved = updated => {
    setDefinitions(prev => prev.map(d => (d._id === updated._id ? updated : d)));
  };

  useEffect(() => {
    setSelectedModels(Array.isArray(activeDefinition?.models) ? [...activeDefinition.models] : []);
  }, [activeId]);

  const toggleEntityModel = modelName => {
    setSelectedModels(prev =>
      prev.includes(modelName) ? prev.filter(model => model !== modelName) : [...prev, modelName]
    );
  };

  useDebouncedEffect(
    () => {
      if (!activeId) return;

      const savedModels = Array.isArray(activeDefinition?.models) ? activeDefinition.models : [];
      const unchanged =
        selectedModels.length === savedModels.length && selectedModels.every(model => savedModels.includes(model));
      if (unchanged) return;

      (async () => {
        const { response, error } = await makeApiRequests({
          endpoint: ENDPOINTS.ENTITY_DEFINITION_WITH_ID(activeId),
          method: 'PUT',
          requestBody: { models: selectedModels }
        });

        if (error) {
          toast.error(error);
          setSelectedModels(savedModels);
          return;
        }

        onColumnsSaved(response);
      })();
    },
    [selectedModels, activeId],
    1000
  );

  const renameEntity = async (definition, name) => {
    if (editingIdRef.current !== definition._id) return;
    editingIdRef.current = null;
    setEditingId(null);

    const trimmed = (name || '').trim();
    if (!trimmed || trimmed === definition.name) return;

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.ENTITY_DEFINITION_WITH_ID(definition._id),
      method: 'PUT',
      requestBody: { name: trimmed }
    });

    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Entity renamed.');
    onColumnsSaved(response);
  };

  const deleteEntity = async () => {
    if (!toBeDeleted) return;
    setDeleteInProgress(true);
    const { error } = await makeApiRequests({
      endpoint: ENDPOINTS.ENTITY_DEFINITION_WITH_ID(toBeDeleted._id),
      method: 'DELETE'
    });
    setDeleteInProgress(false);

    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Entity deleted.');
    setToBeDeleted(null);
    await fetchDefinitions();
  };

  const hasColumns = activeDefinition?.columns?.length > 0;
  const headers = useMemo(
    () =>
      hasColumns
        ? [...activeDefinition.columns.map(columnToHeader), { name: 'Added On', key: 'createdAt', type: 'date' }]
        : [],
    [activeDefinition, hasColumns]
  );
  const formRows = useMemo(
    () => (hasColumns ? getRows(activeDefinition.columns.map(columnToHeader), ['createdAt']) : []),
    [activeDefinition, hasColumns]
  );
  const searchKeys = useMemo(() => (hasColumns ? activeDefinition.columns.map(c => c.key) : []), [
    activeDefinition,
    hasColumns
  ]);

  return (
    <Container fluid className="h-100 py-3 px-md-3">
      <Row className="h-100">
        <Col md={2} className="mb-3 mb-md-0">
          <Card
            className="d-flex flex-column"
            style={{ height: 'calc(100vh - 100px)', maxHeight: 'calc(100vh - 100px)', position: 'sticky', top: 16 }}
          >
            <Card.Body className="d-flex flex-column p-0 overflow-hidden" style={{ minHeight: 0 }}>
              <div className="d-flex justify-content-between align-items-center px-3 py-3 border-bottom flex-shrink-0 bg-light">
                <h6 className="mb-0">Custom Entities</h6>
                <Button size="sm" variant="success" onClick={() => setShowCreateModal(true)}>
                  <PlusCircle className="mr-1" /> New
                </Button>
              </div>

              {loading ? (
                <div className="p-3">
                  <Loader />
                </div>
              ) : definitions.length === 0 ? (
                <div className="p-3 text-muted midFont">No entities yet. Create one to get started.</div>
              ) : (
                <ListGroup variant="flush" className="flex-grow-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                  {definitions.map(definition => (
                    <ListGroup.Item
                      key={definition._id}
                      action
                      active={definition._id === activeId}
                      onClick={() => {
                        setShowSchemaSidebar(false);
                        setActiveId(definition._id);
                      }}
                      className="d-flex justify-content-between align-items-center"
                    >
                      {editingId === definition._id ? (
                        <Form.Control
                          size="sm"
                          autoFocus
                          value={editingName}
                          className="mr-2"
                          onClick={e => e.stopPropagation()}
                          onChange={e => setEditingName(e.target.value)}
                          onBlur={() => renameEntity(definition, editingName)}
                          onKeyDown={e => {
                            e.stopPropagation();
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              renameEntity(definition, editingName);
                            }
                            if (e.key === 'Escape') renameEntity(definition, definition.name);
                          }}
                        />
                      ) : (
                        <span className="text-truncate pr-2">{definition.name}</span>
                      )}
                      <div className="d-flex flex-shrink-0">
                        {editingId === definition._id ? (
                          <Button
                            size="sm"
                            variant={definition._id === activeId ? 'outline-light' : 'outline-success'}
                            className="px-1 py-0 mr-1"
                            title="Confirm rename"
                            onMouseDown={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              renameEntity(definition, editingName);
                            }}
                          >
                            <CheckLg size={12} />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant={definition._id === activeId ? 'outline-light' : 'outline-secondary'}
                            className="px-1 py-0 mr-1"
                            title="Rename entity"
                            onClick={e => {
                              e.stopPropagation();
                              editingIdRef.current = definition._id;
                              setEditingId(definition._id);
                              setEditingName(definition.name);
                            }}
                          >
                            <Pen size={11} />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant={definition._id === activeId ? 'outline-light' : 'outline-danger'}
                          className="px-1 py-0"
                          title="Delete entity"
                          onClick={e => {
                            e.stopPropagation();
                            setToBeDeleted(definition);
                          }}
                        >
                          <Trash size={11} />
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={10}>
          {loading ? (
            <Card>
              <Card.Body>
                <Loader />
              </Card.Body>
            </Card>
          ) : !activeDefinition ? (
            <Card>
              <Card.Body className="text-muted midFont">
                Select an entity from the sidebar, or create a new one.
              </Card.Body>
            </Card>
          ) : (
            <>
              <Card className="mb-3">
                <Card.Body className="py-3">
                  <div className="d-flex flex-wrap align-items-center justify-content-between">
                    <div className="d-flex flex-wrap align-items-center mb-2 mb-md-0">
                      <h6 className="largeFont mb-0 mr-2">Show On:</h6>
                      {ENTITY_MODELS.map(modelName => {
                        const active = selectedModels.includes(modelName);
                        return (
                          <Badge
                            key={modelName}
                            pill
                            className={`px-2 py-1 mr-1 pointer ${active ? '' : 'border'} d-flex align-items-center`}
                            variant={active ? 'primary' : 'light'}
                            style={{ cursor: 'pointer', fontWeight: 500 }}
                            onClick={() => toggleEntityModel(modelName)}
                          >
                            {active ? <CheckCircleFill className="mr-1" /> : <Circle className="mr-1" />}
                            {modelName}
                          </Badge>
                        );
                      })}
                    </div>
                    <p className="mb-0 smallFont text-muted">
                      Choose where this entity appears under Show Custom Fields on create/update forms.
                    </p>
                  </div>
                </Card.Body>
              </Card>

              {hasColumns ? (
                <EntityCRUD
                  key={`${activeDefinition._id}-${activeDefinition.columns.map(c => c.key).join('-')}`}
                  entityName={activeDefinition.name}
                  endpoint={ENDPOINTS.ENTITY_DEFINITION_RECORDS(activeDefinition._id)}
                  entityTableHeaders={headers}
                  entityTableSearchKeys={searchKeys}
                  addFormRows={formRows}
                  updateFormRows={formRows}
                  containerClassName="p-0"
                  headerActions={
                    <Button size="sm" variant="outline-secondary" onClick={() => setShowSchemaSidebar(true)}>
                      <Pen className="mr-1" size={12} /> Edit Columns
                    </Button>
                  }
                />
              ) : (
                <Card>
                  <Card.Body className="text-center py-5">
                    <h6 className="mb-2">{activeDefinition.name}</h6>
                    <p className="text-muted midFont mb-3">
                      This entity has no fields yet. Define a schema to start adding rows.
                    </p>
                    <Button size="sm" variant="primary" onClick={() => setShowSchemaSidebar(true)}>
                      Define Columns
                    </Button>
                  </Card.Body>
                </Card>
              )}

              <SchemaEditorSidebar
                show={showSchemaSidebar}
                definition={activeDefinition}
                onHide={() => setShowSchemaSidebar(false)}
                onSaved={onColumnsSaved}
                saving={savingColumns}
                setSaving={setSavingColumns}
              />
            </>
          )}
        </Col>
      </Row>

      <CreateEntityModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSave={createEntity}
        saving={savingEntity}
      />

      <AlertModal
        show={toBeDeleted !== null}
        alertText={`Are you sure you want to delete "${toBeDeleted?.name}"? All rows in this entity will be permanently deleted.`}
        onHide={() => setToBeDeleted(null)}
        onDismissClick={() => setToBeDeleted(null)}
        onContinueClick={deleteEntity}
        progressText="Deleting entity..."
        showProgress={deleteInProgress}
      />
    </Container>
  );
};

export default CustomEntities;
